import { formatDateOnly } from '@longy/common'
import { useTranslation } from 'react-i18next'

type Props = {
    price: number
    originalPrice?: number | null
    deadline?: string | null
    format: (value: number) => string
    priceClassName?: string
    originalPriceClassName?: string
    align?: 'start' | 'end'
    suffix?: string
    showLabel?: boolean
    showDeadline?: boolean
}

export const EarlyBirdPrice = ({
    price,
    originalPrice,
    deadline,
    format,
    priceClassName = 'text-xl font-bold text-primary',
    originalPriceClassName = 'text-sm font-medium text-brand-gray line-through',
    align = 'start',
    suffix,
    showLabel = false,
    showDeadline = true,
}: Props) => {
    const { t } = useTranslation()
    // Having concluded the item is free, this renders the label itself rather than asking the
    // injected formatter for an amount — a caller that forgot a free-aware formatter would
    // otherwise print "0 Ft" from a component that already knows better. A free price still keeps
    // the range marker when there is one, since the cheapest of several tiers being free does not
    // make the event free; it just carries its own phrasing, because the numeric "-tól" suffix
    // cannot be glued onto a word. Nothing else decorates it: no original to strike through and
    // no early bird discount to name.
    const isFree = price === 0
    const isEarlyBird = !isFree && typeof originalPrice === 'number' && originalPrice > price
    const freeLabel = suffix ? t('frontend.events.freePriceFrom') : t('common.ui.freeOfCharge')

    return (
        <div className={`flex flex-col gap-0.5 ${align === 'end' ? 'items-end' : 'items-start'}`}>
            <div
                className={`flex flex-wrap items-baseline gap-x-2 gap-y-0.5 ${align === 'end' ? 'justify-end' : ''}`}
            >
                {isEarlyBird && (
                    <span className={originalPriceClassName}>{format(originalPrice)}</span>
                )}
                {isEarlyBird && showLabel && (
                    <span className="text-sm font-semibold text-brand-gray">
                        {t('frontend.events.earlyBird')}:
                    </span>
                )}
                <span className={priceClassName}>
                    {isFree ? freeLabel : format(price)}
                    {isFree ? null : suffix}
                </span>
            </div>
            {isEarlyBird && showDeadline && deadline && (
                <span className="text-xs font-medium text-brand-gray">
                    {t('frontend.events.earlyBirdDeadline', { date: formatDateOnly(deadline) })}
                </span>
            )}
        </div>
    )
}
